import { Building2, User, Clock } from 'lucide-react';
import type { Deal } from '../../hooks/useDeals';
import { Badge } from '../shared/Badge';
import { cn } from '../../lib/cn';

interface DealCardProps {
  deal: Deal;
  onClick: (deal: Deal) => void;
}

function daysSince(dateStr?: string): number | null {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr).getTime();
  return Math.floor(diff / 86_400_000);
}

function formatCurrency(value?: number): string {
  if (value == null) return '--';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
}

export function DealCard({ deal, onClick }: DealCardProps) {
  const daysInStage = daysSince(deal.stageEnteredAt);
  const initials = deal.ownerName
    ? deal.ownerName
        .split(' ')
        .map((w) => w[0])
        .join('')
        .slice(0, 2)
    : null;

  return (
    <button
      type="button"
      onClick={() => onClick(deal)}
      className={cn(
        'w-full rounded-lg border border-surface-200 bg-white p-3 text-left shadow-sm',
        'transition-all hover:border-brand-200 hover:shadow-md',
        deal.status === 'won' && 'border-green-200 bg-green-50/30',
        deal.status === 'lost' && 'border-red-200 bg-red-50/30 opacity-70',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <h4 className="text-sm font-semibold text-slate-900 line-clamp-2">{deal.name}</h4>
        {deal.premium != null && (
          <span className="shrink-0 text-sm font-bold text-slate-700">
            {formatCurrency(deal.premium)}
          </span>
        )}
      </div>

      {deal.organizationName && (
        <div className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-500">
          <Building2 className="h-3 w-3" />
          <span className="truncate">{deal.organizationName}</span>
        </div>
      )}

      <div className="mt-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {initials && (
            <div className="flex h-5 w-5 items-center justify-center rounded-full bg-brand-100 text-2xs font-semibold text-brand-700">
              {initials}
            </div>
          )}
          {daysInStage != null && (
            <div className="flex items-center gap-1 text-2xs text-slate-400">
              <Clock className="h-3 w-3" />
              {daysInStage}d
            </div>
          )}
        </div>

        <div className="flex items-center gap-1">
          {deal.tags?.slice(0, 2).map((tag) => (
            <Badge key={tag} color="slate" size="sm">{tag}</Badge>
          ))}
        </div>
      </div>
    </button>
  );
}
